/**
 * Обзор района на каноне — перевод `/overview/district` и сводки
 * `/season/picture` в блоки экрана.
 *
 * Главное расхождение с прототипом — очередь внимания. Пункт прототипа
 * несёт вид (`kind`), дату «наблюдается с» (`since`) и ссылку на сущность
 * (контур, хозяйство, работу). Пункт канона — нет: у него ранг, заголовок,
 * счёт в своём знаменателе (`noun`), риск, действие и ссылка на РАЗДЕЛ, где
 * пункт разбирается. Здесь эти поля НЕ досочиняются: вид не угадывается по
 * ключу, дата не подставляется датой среза, сущность не выбирается «первой
 * попавшейся». Экран показывает пункт в той форме, что даёт сервер, и говорит
 * словами, чего у него нет.
 */

import type { Schemas } from '../../../api/client';
import type { QueueItemView } from '../../pages/overviewParts';
import { countOf, haUnit, num } from '../../lib/format';
import { levelSpec } from '../../lib/levels';
import { measuredView, microhaToHa, noData, type MeasuredView } from './compare';
import { defineMessages } from '../../../i18n/define';
import { tr } from '../../../i18n/runtime';

const m = defineMessages({
  mixed: {
    id: 'data.overview.mixedClasses',
    defaultMessage: 'собрано из данных разных классов',
    description: 'Пометка пункта очереди внимания: число собрано из данных разных классов достоверности (строчная фраза)',
  },
  countNone: {
    id: 'data.overview.countNone',
    defaultMessage: 'счёт: {noData}',
    description: 'Начало строки пункта очереди внимания, когда число не названо; {noData} — «нет данных»',
  },
  body: {
    id: 'data.overview.body',
    defaultMessage: '{parts}. {risk}',
    description: 'Текст пункта очереди внимания на странице «Район»; {parts} — число, площадь и пометка через « · », {risk} — формулировка риска от сервера',
  },
  foot: {
    id: 'data.overview.foot',
    defaultMessage: 'Ранг {rank} · наблюдается с: {noData} — дату начала база района не отдаёт',
    description: 'Нижняя строка пункта очереди внимания; {rank} — место в очереди, {noData} — «нет данных»',
  },
  footNoLink: {
    id: 'data.overview.footNoLink',
    defaultMessage: 'Ранг {rank} · наблюдается с: {noData} — дату начала база района не отдаёт · адреса у позиции нет',
    description: 'Нижняя строка пункта очереди внимания, когда у позиции нет ссылки на раздел; {rank} — место в очереди, {noData} — «нет данных»',
  },
});


type AttentionItem = Schemas['AttentionItemOut'];
type LandRow = Schemas['LandCategoryOut'];

/* ---------------------------------------------------------------- земля */

export interface LandView {
  code: string;
  name: string;
  ha: number | null;
  measured: MeasuredView;
}

/** Строка земли по коду; нет строки — нет величины, а не ноль. */
export function landOf(rows: ReadonlyArray<LandRow>, code: string): LandView | null {
  const row = rows.find((r) => r.code === code);
  if (!row) return null;
  return {
    code: row.code,
    name: row.name,
    ha: microhaToHa(row.area.value),
    measured: measuredView(row.area),
  };
}

/* -------------------------------------------------------------- внимание */

export interface AttentionLine {
  key: string;
  rank: number;
  title: string;
  /** Счёт в знаменателе пункта словами; null — величины нет. */
  count: number | null;
  noun: [string, string, string] | null;
  areaHa: number | null;
  risk: string;
  action: string;
  /** Ссылка сервера на раздел разбора — не на сущность. */
  link: string | null;
  /** Подпись класса: синтетика — словами; смесь классов — отдельно. */
  note: string | null;
}

function nounOf(noun: ReadonlyArray<string>): [string, string, string] | null {
  return noun.length === 3 ? [noun[0]!, noun[1]!, noun[2]!] : null;
}

export function attentionLine(item: AttentionItem): AttentionLine {
  const measured = measuredView(item.measured);
  return {
    key: item.key,
    rank: item.rank,
    title: item.title,
    count: measured.value,
    noun: nounOf(item.noun),
    areaHa: microhaToHa(item.area_microha),
    risk: item.risk,
    action: item.action,
    link: item.link ? item.link : null,
    note:
      item.evidence === 'mixed'
        ? tr(m.mixed)
        : measured.note,
  };
}

/** Пункт канона → строка очереди K2. Вида и даты у пункта нет — так и сказано. */
export function canonQueueItem(line: AttentionLine): QueueItemView {
  const count =
    line.count === null
      ? tr(m.countNone, { noData: noData() })
      : line.noun
        ? countOf(line.count, line.noun)
        : num(line.count);
  return {
    key: line.key,
    kind: null,
    // Пункт обзора района считается в знаменателе района целиком.
    level: levelSpec('district').caption,
    title: line.title,
    href: line.link,
    body: tr(m.body, {
      parts: [count, line.areaHa === null ? null : haUnit(line.areaHa), line.note || null].filter(Boolean).join(' · '),
      risk: line.risk,
    }),
    foot: tr(line.link ? m.foot : m.footNoLink, { rank: line.rank, noData: noData() }),
  };
}


/** Объём воды строкой канона (`"28708612.480"`) → число; мусор — величины нет. */
export function volumeM3(raw: string | number | null | undefined): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  const value = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(value) ? value : null;
}
